/**
 * @voltxampere/engine — Potentiometer 3-pin linear stamp (Sprint 23 B4)
 *
 * Üç terminalli ayarlanabilir direnç. İçten iki seri direnç:
 *
 *   R_tw = R_total · position           (top ↔ wiper)
 *   R_wb = R_total · (1 - position)     (wiper ↔ bottom)
 *
 * MNA stamp 6 hücre (3 düğüm tüm permütasyonları):
 *
 *   Y[t, t] += G_tw
 *   Y[w, w] += G_tw + G_wb
 *   Y[b, b] += G_wb
 *   Y[t, w] -= G_tw    Y[w, t] -= G_tw
 *   Y[w, b] -= G_wb    Y[b, w] -= G_wb
 *
 * Sayısal sınır (`position` ≈ 0 veya 1): R sıfıra giderse G sonsuza
 * kaçar ve LU singüler olur. Schema validation `position`'u
 * (ε, 1-ε) aralığına sınırlandırır; stamp ek defansif clamp
 * uygular (kötü niyetli/eski fixture'lar için).
 *
 * Pure conductance — augmented row YOK; transient + AC stamp aynı
 * conductance'ı kopyalar (frekans-bağımsız).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { StampParameterError } from "@voltxampere/shared";
import { defineDCStamp } from "../definition.js";
import { getTerminalNetId } from "../terminal-utils.js";
const POSITION_EPSILON = 0.001;
const DEFAULT_RESISTANCE = 10_000;
const DEFAULT_POSITION = 0.5;
export function resolvePotentiometerParams(componentId, params) {
    const rRaw = params.resistance;
    const r = typeof rRaw === "number" && rRaw > 0 && Number.isFinite(rRaw) ? rRaw : DEFAULT_RESISTANCE;
    const pRaw = params.position;
    let p = typeof pRaw === "number" && Number.isFinite(pRaw) ? pRaw : DEFAULT_POSITION;
    // Defansif clamp — schema zaten (ε, 1-ε) sınırlandırır ama runtime
    // legacy fixture'lar için.
    if (p < POSITION_EPSILON)
        p = POSITION_EPSILON;
    if (p > 1 - POSITION_EPSILON)
        p = 1 - POSITION_EPSILON;
    if (!Number.isFinite(r) || r <= 0) {
        // Sprint 55 typed-error migration.
        throw new StampParameterError(`potentiometerDCStamp: component "${componentId}" has non-positive resistance (got ${String(r)})`, {
            meta: {
                stampType: "potentiometer",
                parameter: "resistance",
                received: r,
                expected: "> 0 and finite",
                componentId,
            },
        });
    }
    return { resistance: r, position: p };
}
export const potentiometerDCStamp = (ctx, component) => {
    const { resistance, position } = resolvePotentiometerParams(component.id, component.params);
    const G_tw = 1 / (resistance * position);
    const G_wb = 1 / (resistance * (1 - position));
    const t = ctx.nodeIndex(getTerminalNetId(component, "top"));
    const w = ctx.nodeIndex(getTerminalNetId(component, "wiper"));
    const b = ctx.nodeIndex(getTerminalNetId(component, "bottom"));
    ctx.addY(t, t, G_tw);
    ctx.addY(w, w, G_tw + G_wb);
    ctx.addY(b, b, G_wb);
    ctx.addY(t, w, -G_tw);
    ctx.addY(w, t, -G_tw);
    ctx.addY(w, b, -G_wb);
    ctx.addY(b, w, -G_wb);
};
export const potentiometerDCStampDefinition = defineDCStamp("potentiometer", potentiometerDCStamp, []);
//# sourceMappingURL=potentiometer.js.map